import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { History } from 'history';

import {
  LandlordFullPageProps,
  LandlordFunnelState,
  LandlordFormAnswer,
} from 'scenes/landlord/types';
import {
  useQuoteFlowDispatch,
  useFunnelState,
} from 'scenes/common/contexts/quoteFlowContext';
import useActions from 'scenes/landlord/hooks/useActions';
import { useReferenceData } from 'scenes/landlord/hooks/useReferenceData';
import analytics from 'scenes/landlord/analytics';
import { useMarketingCampaignParams } from 'scenes/common/hooks/useMarketingCampaignParams';
import { useCoverTypeParam } from 'scenes/landlord/hooks/useCoverTypeParam';
import { mapCoverType } from 'scenes/landlord/actions';
import { getApiData } from 'scenes/common/helpers/apiData';
import { CoverType, FunnelType, InsuranceName } from 'scenes/common/types';
import { useSystemConfig } from 'scenes/common/hooks/useSystemConfig';
import { isUnderMaintenance } from 'scenes/common/selectors/configSelectors';
import redirectToOutagePage from 'scenes/outage/helpers/config';

export const withLandlordFunnel = (
  Component: FunctionComponent<LandlordFullPageProps>
): FunctionComponent => () => {
  const funnelState = useFunnelState<LandlordFunnelState>();
  const dispatch = useQuoteFlowDispatch();

  const handleAnswer = useCallback(
    (answer: LandlordFormAnswer) => {
      // Enable this if you need to debug actions
      // console.log('Received', answer, funnelState.step);
      dispatch({ ...answer, sourceStep: funnelState.step });
    },
    [dispatch, funnelState.step]
  );

  const referenceData = useReferenceData();

  const {
    onNext,
    onNextWithReset,
    onPrevious,
    onCreateQuote,
    onCompleteQuote,
    onSaveQuote,
    onResumeQuote,
    onRestartQuote,
    onClearApiErrors,
    onUpdateQuoteDetails,
    onUpdateContentsExcess,
    onUpdateBuildingExcess,
    onUpdateOptionalExtras,
    onAddAdditionalPolicyHolder,
    onRemoveAdditionalPolicyHolder,
    onUpdateAdditionalPolicyHolder,
    onUpdatePaymentDate,
    onBpayPayment,
    onDirectDebitPayment,
    onCreditCardPayment,
    onUpdatePromoCode,
    onSetCoverType,
    onSetPrefilledPromoCode,
    onSetColesCampaignCode,
    onRetrieveCampaign,
    onUpdateKickOutChoice,
    onEditQuoteDetails,
    onEditCoverDetails,
    onEditPolicyDetails,
    onSwitchPaymentFrequency,
    onGetConfig,
    onUpdateToPolicyDetails,
    onUpdatePolicyStartDate,
    onUpdateFlybuys,
    onResetPropertyBuiltYear,
  } = useActions(handleAnswer, getApiData(referenceData));

  const [showSaveQuoteModal, setShowSaveQuoteModal] = useState(false);

  const history: History = useHistory();

  const [qid, pid, cid] = useMarketingCampaignParams(
    onSetPrefilledPromoCode,
    onRetrieveCampaign,
    onSetColesCampaignCode,
    InsuranceName.Landlord
  );

  useSystemConfig(onGetConfig);

  const coverTypeFromUrl = useCoverTypeParam(onSetCoverType);

  const onClickSaveQuoteModal = () => {
    if (!showSaveQuoteModal) {
      analytics().saveQuote(funnelState);
    }
    setShowSaveQuoteModal(!showSaveQuoteModal);
  };

  const isPreselectedCoverTypeSetFromUrl = (
    urlCoverType: CoverType | undefined,
    preselectedCoverType: CoverType
  ): boolean => urlCoverType === preselectedCoverType;

  useEffect(() => {
    if (
      isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Landlord)
    ) {
      redirectToOutagePage(history);
    }
  }, [funnelState, history]);

  useEffect(() => {
    // This condition makes sure the events are only fired when query parameters match the state
    // this avoids it being fired before the state is initialised
    if (
      (!coverTypeFromUrl ||
        isPreselectedCoverTypeSetFromUrl(
          mapCoverType(coverTypeFromUrl),
          funnelState.quoteDetails.coverType
        )) &&
      qid === funnelState.quoteDetails.prefilledCampaignId &&
      pid === funnelState.quoteDetails.prefilledPromoCode &&
      cid === funnelState.quoteDetails.colesCampaignCode
    ) {
      analytics().viewLoaded(funnelState, getApiData(referenceData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coverTypeFromUrl,
    qid,
    pid,
    cid,
    history,
    funnelState.step,
    funnelState.flow,
    funnelState.quoteDetails.coverType,
    funnelState.quoteDetails.prefilledCampaignId,
    funnelState.quoteDetails.prefilledPromoCode,
    funnelState.quoteDetails.colesCampaignCode,
  ]);

  return (
    <Component
      onNext={onNext}
      onPrevious={onPrevious}
      funnelState={funnelState}
      flowType={funnelState.flow}
      stepType={funnelState.step}
      referenceData={referenceData}
      onCreateQuote={onCreateQuote}
      onCompleteQuote={onCompleteQuote}
      onSaveQuote={onSaveQuote}
      onResumeQuote={onResumeQuote}
      onRestartQuote={onRestartQuote}
      onClearApiErrors={onClearApiErrors}
      onUpdateQuoteDetails={onUpdateQuoteDetails}
      onUpdateBuildingExcess={onUpdateBuildingExcess}
      onUpdateContentsExcess={onUpdateContentsExcess}
      onUpdateOptionalExtras={onUpdateOptionalExtras}
      onAddAdditionalPolicyHolder={onAddAdditionalPolicyHolder}
      onRemoveAdditionalPolicyHolder={onRemoveAdditionalPolicyHolder}
      onUpdateAdditionalPolicyHolder={onUpdateAdditionalPolicyHolder}
      onUpdatePaymentDate={onUpdatePaymentDate}
      onBpayPayment={onBpayPayment}
      onDirectDebitPayment={onDirectDebitPayment}
      onCreditCardPayment={onCreditCardPayment}
      onClickSaveQuoteModal={onClickSaveQuoteModal}
      showSaveQuoteModal={showSaveQuoteModal}
      onUpdatePromoCode={onUpdatePromoCode}
      onUpdateKickOutChoice={onUpdateKickOutChoice}
      onEditQuoteDetails={onEditQuoteDetails}
      onEditCoverDetails={onEditCoverDetails}
      onEditPolicyDetails={onEditPolicyDetails}
      onSwitchPaymentFrequency={onSwitchPaymentFrequency}
      onUpdateToPolicyDetails={onUpdateToPolicyDetails}
      onUpdatePolicyStartDate={onUpdatePolicyStartDate}
      onUpdateFlybuys={onUpdateFlybuys}
      onNextWithReset={onNextWithReset}
      onResetPropertyBuiltYear={onResetPropertyBuiltYear}
    />
  );
};

export default withLandlordFunnel;
